/**
 * H-88 (Pivot): Runtime feature flags hook.
 *
 * Fetches flags from `getClientConfig` once per session and caches the result
 * at module scope so every consumer (nav, route gates, UI components) sees a
 * consistent view. Defaults are conservative — anything not yet returned by
 * the server is treated as `false` so a missing/older CF deploy never
 * accidentally enables a flagged surface.
 *
 * v1 defaults:
 * - `discoveryEnabled = false` — Discovery feed hidden (H-88)
 * - `complementaryEnabled = false` — secondary "extras" voting hidden (H-90)
 */
import { useEffect, useState } from 'react';
import { fetchClientConfig } from '../services/clientConfig';

export interface FeatureFlags {
  discoveryEnabled: boolean;
  complementaryEnabled: boolean;
  /** M-99: When false, Ticketmaster event search is skipped server-side. Default false. */
  ticketmasterEnabled: boolean;
  /** H-147: When false, H-140–H-146 progressive disclosure UI is hidden; v1 flow is shown. Default false. */
  progressiveDisclosureEnabled: boolean;
}

export const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  discoveryEnabled: false,
  complementaryEnabled: false,
  ticketmasterEnabled: false,
  progressiveDisclosureEnabled: false,
};

let cachedFlags: FeatureFlags | null = null;
const subscribers = new Set<(flags: FeatureFlags) => void>();
let loadPromise: Promise<void> | null = null;

function notifySubscribers(flags: FeatureFlags): void {
  subscribers.forEach((cb) => cb(flags));
}

async function loadFlagsOnce(): Promise<void> {
  if (cachedFlags) return;
  if (loadPromise) return loadPromise;
  loadPromise = (async () => {
    try {
      const config = await fetchClientConfig();
      cachedFlags = {
        discoveryEnabled: config.features?.discoveryEnabled ?? false,
        complementaryEnabled: config.features?.complementaryEnabled ?? false,
        ticketmasterEnabled: config.features?.ticketmasterEnabled ?? false,
        progressiveDisclosureEnabled: config.features?.progressiveDisclosureEnabled ?? false,
      };
    } catch {
      // On fetch failure (offline / not signed in / CF error) keep defaults
      // — Discovery stays off, which is the correct v1 fallback.
      cachedFlags = { ...DEFAULT_FEATURE_FLAGS };
    }
    notifySubscribers(cachedFlags);
  })();
  return loadPromise;
}

export function useFeatureFlags(): FeatureFlags {
  const [flags, setFlags] = useState<FeatureFlags>(() => cachedFlags ?? DEFAULT_FEATURE_FLAGS);

  useEffect(() => {
    // If the cache was populated between this component's render and effect,
    // sync the latest value into local state.
    if (cachedFlags && cachedFlags !== flags) {
      setFlags(cachedFlags);
      return;
    }
    if (cachedFlags) return;
    subscribers.add(setFlags);
    void loadFlagsOnce();
    return () => {
      subscribers.delete(setFlags);
    };
    // `flags` intentionally omitted — we only sync once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return flags;
}

/** Test-only helper — clears module-level cache and subscribers. */
export function _resetFeatureFlagsForTest(): void {
  cachedFlags = null;
  subscribers.clear();
  loadPromise = null;
}

/**
 * Test-only helper — preset cached flags so consumers see them synchronously.
 * Accepts a partial so tests can set just the flag they care about; missing
 * fields fall back to `DEFAULT_FEATURE_FLAGS` (everything off).
 */
export function _setFeatureFlagsForTest(flags: Partial<FeatureFlags>): void {
  cachedFlags = { ...DEFAULT_FEATURE_FLAGS, ...flags };
}
